// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { createColumnHelper, type ExpandedState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Table } from '@shared/ui-kit/components';
import type { GroupedCreditLinesTableRow } from '@shared/advisory/aggregation/utils/build-credit-lines-table-rows';
import { CreditLineUsageProgress } from './credit-line-usage-progress';

export interface CreditLinesTableProps {
  readonly data: GroupedCreditLinesTableRow[];
  readonly currency: string;
}

const columnHelper = createColumnHelper<GroupedCreditLinesTableRow>();

export const useCreditLinesTable = ({ currency }: CreditLinesTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('bankName', {
        header: t('aggregator.loans.product', { ns: 'aggregator' }),
        cell: ({ row }) => (
          <div className="flex items-center gap-x-2 truncate">
            {row.getCanExpand() ? (
              <>
                <Table.Expander expander={row} />
                <span>{row.original.bankName}</span>
              </>
            ) : (
              <span className="pl-6">{row.original.productName}</span>
            )}
          </div>
        ),
      }),

      columnHelper.accessor('limit', {
        header: t('aggregator.loans.credit-limit', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            value={info.getValue()}
            currency={currency}
            safeValue="-"
          />
        ),
      }),

      columnHelper.accessor('disposed', {
        header: t('aggregator.loans.utilized-balance', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            value={info.getValue()}
            currency={currency}
            safeValue="-"
          />
        ),
      }),

      columnHelper.accessor('available', {
        header: t('aggregator.loans.available-balance', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            value={info.getValue()}
            currency={currency}
            safeValue="-"
          />
        ),
      }),

      columnHelper.accessor('usageRatio', {
        header: t('aggregator.loans.usage', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => {
          const ratio = info.getValue();

          if (ratio === null) {
            return <span>-</span>;
          }

          return (
            <div className="flex min-w-[8rem] items-center gap-x-2">
              <CreditLineUsageProgress ratio={ratio} />
              <Table.Data
                format="percentage"
                value={ratio}
                formatOptions={{
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }}
                safeValue="-"
                container={false}
              />
            </div>
          );
        },
      }),

      columnHelper.accessor('maturityDate', {
        header: t('common.expiration-date', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: info => {
          if (info.row.getCanExpand()) {
            return null;
          }

          return (
            <Table.Data
              format="date"
              value={info.getValue()}
              formatOptions={{ day: 'numeric', month: 'short', year: 'numeric' }}
              safeValue="-"
            />
          );
        },
      }),
    ],
    [currency, t],
  );

  const initialExpanded: ExpandedState = true;

  return {
    columns,
    initialExpanded,
  };
};
